import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { ApiSuccessEnvelope } from "../../types/affiliates";
import {
  getAnalyticsInstance,
  type AffiliateAnalyticsDataSource,
} from "./analyticsDataSource";
import type { AffiliateChartRange } from "./useAffiliateTimeSeries";
import { toNumberMetrics } from "./useAffiliateRankings";
import type { AffiliateAnalyticsFilters } from "./useAffiliateKpiCards";

// One US state's metrics. `state` is the 2-letter code (e.g. "CA").
export interface AffiliateGeoItem {
  state: string;
  metrics: Record<string, number>;
}

interface AffiliateGeoData {
  items?: Array<{
    state?: string;
    metrics?: Record<string, number | string | boolean | null>;
  }>;
}

const fetchGeo = async (
  route: string,
  range: AffiliateChartRange,
  metric: string,
  filters: AffiliateAnalyticsFilters,
  dataSource: AffiliateAnalyticsDataSource = "operator",
): Promise<AffiliateGeoItem[]> => {
  const params: Record<string, string> = {
    start_date: range.start_date,
    end_date: range.end_date,
    metric,
  };
  if (filters.affiliate) params.affiliate = filters.affiliate;
  if (filters.promo_code) params.promo_code = filters.promo_code;
  const response = await getAnalyticsInstance(dataSource).get<
    ApiSuccessEnvelope<AffiliateGeoData>
  >(route, { params });
  return (response.data.data?.items ?? []).map((item) => ({
    state: item.state ?? "",
    metrics: toNumberMetrics(item.metrics),
  }));
};

const resolveGeoRoute = (
  dataSource: AffiliateAnalyticsDataSource,
  affiliateId?: string,
): string => {
  // Portal routes carry no affiliate_id — the token scopes the query.
  if (dataSource === "portal") {
    return API_ROUTES.AFFILIATE_PORTAL_ANALYTICS_GEO;
  }
  return affiliateId
    ? API_ROUTES.AFFILIATE_DETAIL_ANALYTICS_GEO.replace(
        "{affiliate_id}",
        encodeURIComponent(affiliateId),
      )
    : API_ROUTES.AFFILIATE_ANALYTICS_GEO;
};

// State-level metrics for the geo map. `affiliateId` present → the
// per-affiliate endpoint; absent → module level (all affiliates).
// `options.enabled: false` skips the fetch — for previews where the
// module fallback would be wrong.
export const useAffiliateGeo = (
  range: AffiliateChartRange,
  metric: string,
  affiliateId?: string,
  filters: AffiliateAnalyticsFilters = {},
  dataSource: AffiliateAnalyticsDataSource = "operator",
  options: { enabled?: boolean } = {},
): UseQueryResult<AffiliateGeoItem[], Error> =>
  useQuery<AffiliateGeoItem[], Error>({
    queryKey: [
      "affiliate-kpis",
      "geo",
      dataSource,
      affiliateId ?? "",
      metric,
      range.start_date,
      range.end_date,
      filters.affiliate ?? null,
      filters.promo_code ?? null,
    ],
    queryFn: () =>
      fetchGeo(
        resolveGeoRoute(dataSource, affiliateId),
        range,
        metric,
        filters,
        dataSource,
      ),
    enabled:
      (options.enabled ?? true) && Boolean(range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

export { fetchGeo };
